import { create } from "zustand";
import type { TemplateTask, TemplateStep } from "@/types/task";
import { useModalStore } from "./modalStore";

interface TemplateData {
  name: string;
  description: string;
  layer_name: string;
  category_id: string;
  status_id: string;
  priority_id: string;
  default_assignee_type?: "User" | "Team";
  default_assignee?: string;
  default_due_days?: number;
  default_start_days?: number;
  auto_assign_steps?: boolean;
  require_photos?: boolean;
  require_geometry?: boolean;
  allowed_geometry_types?: ("Point" | "LineString" | "Polygon")[];
}

interface TemplateStore {
  // State (modal states moved to modalStore)
  newTemplateData: TemplateData;
  templateSteps: TemplateStep[];
  validationError: string | null;

  // Basic setters
  setNewTemplateData: (data: Partial<TemplateData>) => void;
  setTemplateSteps: (steps: TemplateStep[]) => void;
  setValidationError: (error: string | null) => void;

  // Composite actions
  startCreateTemplate: () => void;
  startEditTemplate: (template: TemplateTask) => void;
  cancelTemplateForm: () => void;
  resetTemplateForm: () => void;

  // Validation function
  validateTemplateData: () => boolean;
}

const createEmptyTemplateData = (): TemplateData => ({
  name: "",
  description: "",
  layer_name: "",
  category_id: "",
  status_id: "",
  priority_id: "",
  default_assignee_type: undefined,
  default_assignee: undefined,
  default_due_days: undefined,
  default_start_days: undefined,
  auto_assign_steps: false,
  require_photos: false,
  require_geometry: false,
  allowed_geometry_types: undefined,
});

export const useTemplateStore = create<TemplateStore>((set, get) => ({
  // Initial state (modal states removed - managed by modalStore)
  newTemplateData: createEmptyTemplateData(),
  templateSteps: [],
  validationError: null,

  // Basic setters
  setNewTemplateData: (data) =>
    set({ newTemplateData: { ...get().newTemplateData, ...data } }),
  setTemplateSteps: (steps) => set({ templateSteps: steps }),
  setValidationError: (error) => set({ validationError: error }),
  // Composite actions
  startCreateTemplate: () => {
    set({
      newTemplateData: createEmptyTemplateData(),
      templateSteps: [],
      validationError: null,
    });

    // Open the create template modal
    useModalStore.getState().openCreateTemplate();
  },

  startEditTemplate: (template) =>
    set({
      newTemplateData: {
        name: template.name,
        description: template.description || "",
        layer_name: template.layer_name || "",
        category_id: template.category_id || "",
        status_id: template.status_id || "",
        priority_id: template.priority_id || "",
        default_assignee_type: template.default_assignee_type,
        default_assignee: template.default_assignee,
        default_due_days: template.default_due_days,
        default_start_days: template.default_start_days,
        auto_assign_steps: template.auto_assign_steps || false,
        require_photos: template.require_photos || false,
        require_geometry: template.require_geometry || false,
        allowed_geometry_types: template.allowed_geometry_types,
      },
      templateSteps: template.template_steps || [],
      validationError: null,
    }),

  cancelTemplateForm: () =>
    set({
      newTemplateData: createEmptyTemplateData(),
      templateSteps: [],
      validationError: null,
    }),

  resetTemplateForm: () =>
    set({
      newTemplateData: createEmptyTemplateData(),
      templateSteps: [],
      validationError: null,
    }),

  // Validation function
  validateTemplateData: () => {
    const { newTemplateData } = get();
    if (!newTemplateData.name.trim()) {
      set({ validationError: "Template name is required" });
      return false;
    }
    if (!newTemplateData.layer_name.trim()) {
      set({ validationError: "Layer name is required" });
      return false;
    }
    set({ validationError: null });
    return true;
  },
}));
